"use client";

import { Crown, Loader2 } from "lucide-react";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { toast } from "sonner";

import { useSession } from "@/app/hooks/useSession";
import { Button } from "@/components/ui/button";
import { trackSubscription } from "@/lib/analytics";

interface UpgradeButtonProps {
  billingInterval?: "monthly" | "yearly";
  limitType?: "ai" | null;
  className?: string;
  size?: "default" | "sm" | "lg" | "icon";
  children?: React.ReactNode;
  onSuccess?: () => void;
  onLoadingChange?: (isLoading: boolean) => void;
}

export function UpgradeButton({
  billingInterval = "monthly",
  limitType = null,
  className,
  size = "default",
  children,
  onSuccess,
  onLoadingChange,
}: UpgradeButtonProps) {
  const t = useTranslations("premium");
  const { data: session } = useSession();
  const [isLoading, setIsLoading] = useState(false);

  const updateLoading = (loading: boolean) => {
    setIsLoading(loading);
    onLoadingChange?.(loading);
  };

  const handleUpgrade = async () => {
    if (!session?.user) {
      toast.error(t("signInToUpgrade"));
      return;
    }

    updateLoading(true);

    // Track checkout start
    trackSubscription("checkout_started", {
      interval: billingInterval,
      limitType,
    });

    try {
      const response = await fetch("/api/checkout", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ interval: billingInterval }),
      });

      const data = await response.json();

      if (!response.ok) {
        // Special handling for duplicate subscription error
        if (data.error?.includes("already have an active subscription")) {
          toast.error(t("alreadyPremium"));
        } else {
          toast.error(t(data.error as any) || t("checkoutFailed"));
        }
        updateLoading(false);
        return;
      }

      // Call success callback if provided
      onSuccess?.();

      // Redirect to Stripe checkout
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err: any) {
      console.error("Checkout error:", err);
      toast.error(err.message || t("somethingWentWrong"));
      updateLoading(false);
    }
  };

  return (
    <Button className={className} size={size} onClick={handleUpgrade} disabled={isLoading}>
      {isLoading ? (
        <>
          <Loader2 className="w-4 h-4 animate-spin" />
          {size !== "icon" && <span className="ml-2">{t("processing")}</span>}
        </>
      ) : (
        <>
          {children || (
            <>
              <Crown className="w-5 h-5" />
              <span>{t("getPremium")}</span>
            </>
          )}
        </>
      )}
    </Button>
  );
}
